/**
 * LGPD na visão 360 (Task 13, brief 3.4).
 *
 * Endereço CANÔNICO (Task 13, limpeza de arquitetura): promovido de
 * `apps/plataforma/src/crm/lgpd.ts` para `packages/crm`. A Edge Function
 * `supabase/functions/crm-runtime` e o app importam daqui; o arquivo antigo em
 * `apps/plataforma/src/crm/lgpd.ts` reexporta deste módulo.
 *
 * **Reuso de `packages/admin-ops`, não reimplementação** (contrato de design
 * da Task 13): a máscara do cliente anonimizado é `anonymizeCustomer` e o
 * pacote de portabilidade é `buildCustomerDataExport` — as MESMAS operações
 * que a `admin-runtime` executa em `/api/admin/lgpd/*`. Duas versões da
 * anonimização deixariam o 360 exibindo o dado que a outra apagou.
 *
 * As duas razões de isto existir aqui:
 *
 * 1. **Cliente anonimizado não pode reaparecer com nome e telefone.** O
 *    direito ao esquecimento mantém o registro (para pedidos e eventos não
 *    ficarem órfãos) e remove o dado pessoal; o 360 agrega seis fontes e, sem
 *    a máscara, exibiria o contato a partir do cadastro canônico.
 * 2. **Portabilidade do relacionamento.** A exportação da `admin-runtime`
 *    conhece cliente, conversas e leads; o 360 conhece também pedidos
 *    unificados, negociações e propostas. O pacote é montado com a MESMA
 *    função, sobre os registros que esta tela já agregou.
 *
 * Puro e sem I/O: recebe registros e devolve estrutura. Quem persiste o pedido
 * de exportação (`lgpd_requests`) é o runtime/Edge Function.
 */
import { anonymizeCustomer, buildCustomerDataExport } from "../../admin-ops/src/index.js";
/** `customers.customer_status` depois do direito ao esquecimento (admin-ops). */
export const ANONYMIZED_CUSTOMER_STATUS = "anonymized";
export function isAnonymizedCustomer(customer) {
    return customer.status === ANONYMIZED_CUSTOMER_STATUS;
}
/** Cliente canônico no formato aberto que `packages/admin-ops` opera. */
export function toAdminOpsCustomer(customer) {
    return {
        id: customer.id,
        full_name: customer.name,
        phone: customer.phone,
        email: customer.email,
        origin: customer.origin,
        customer_status: customer.status,
        tags: []
    };
}
/**
 * Cliente como o 360 pode exibi-lo.
 *
 * Cliente ativo passa inalterado. Cliente anonimizado volta pela máscara de
 * `anonymizeCustomer` — e os identificadores de canal (`customer_identities`)
 * saem da lista, porque telefone e e-mail apagados do cadastro continuariam
 * legíveis ali.
 */
export function applyLgpdMask(customer) {
    if (!isAnonymizedCustomer(customer))
        return customer;
    const masked = anonymizeCustomer(toAdminOpsCustomer(customer));
    return {
        ...customer,
        name: typeof masked.full_name === "string" ? masked.full_name : "",
        phone: masked.phone ?? null,
        email: masked.email ?? null,
        status: masked.customer_status ?? ANONYMIZED_CUSTOMER_STATUS,
        identities: []
    };
}
/**
 * Pacote de portabilidade do relacionamento, montado por
 * `buildCustomerDataExport`. As propostas entram junto do cliente porque são
 * dado pessoal dele (o que foi oferecido e a que preço), e não um registro
 * interno de operação.
 */
export function buildCrm360Export(input) {
    return buildCustomerDataExport({ ...toAdminOpsCustomer(input.customer), quotes: input.quotes }, {
        conversations: input.conversations,
        leads: input.opportunities,
        orders: input.orders
    });
}
/** Resumo do que o pacote carrega — é o que a tela exibe antes de exportar. */
export function crm360ExportSummary(input) {
    const payload = buildCrm360Export(input);
    return {
        customerId: input.customer.id,
        conversations: payload.conversations.length,
        orders: payload.orders.length,
        opportunities: payload.leads.length,
        quotes: input.quotes.length,
        exportedAt: payload.exportedAt,
        anonymized: isAnonymizedCustomer(input.customer)
    };
}
